import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LogoMark } from "./Logo";
import { copy } from "@/content";

const HERO_IMG =
  "https://images.pexels.com/photos/35696867/pexels-photo-35696867.jpeg";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ lang = "en" }) => {
  const t = copy[lang].hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const goDiscover = (e) => {
    e.preventDefault();
    const el = document.querySelector("#about");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0" aria-hidden="true">
        <div
          className="absolute right-0 top-0 h-full w-full opacity-30 sm:w-[62%] sm:opacity-40"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 45%), linear-gradient(to top, transparent 0%, black 40%)",
            WebkitMaskComposite: "source-in",
            maskImage:
              "linear-gradient(to right, transparent 0%, black 45%), linear-gradient(to top, transparent 0%, black 40%)",
            maskComposite: "intersect",
          }}
        />
        <div className="absolute inset-0 bg-dealer-black/40" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-24 pt-40 sm:px-10 sm:pb-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-10 flex items-center gap-4"
        >
          <LogoMark size={34} />
          <span className="dealer-eyebrow">{t.label}</span>
        </motion.div>

        <h1 className="max-w-5xl font-serif text-4xl leading-[1.12] tracking-tight text-dealer-ivory sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.5}>THE RIGHT PEOPLE.</MaskedLine>
          <MaskedLine delay={0.65}>THE RIGHT OPPORTUNITIES.</MaskedLine>
          <MaskedLine delay={0.8}>
            THE RIGHT <span className="text-dealer-burgundybright">DEAL.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl text-sm font-light leading-relaxed text-dealer-silver sm:text-base"
          data-testid="hero-subcopy"
        >
          {t.body}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12"
        >
          <a
            href="#about"
            onClick={goDiscover}
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-4 border border-dealer-ivory/25 px-8 py-4 font-sans text-[11px] font-medium tracking-[0.3em] text-dealer-ivory transition-colors duration-500 hover:border-dealer-ivory/70"
          >
            {t.cta}
            <span className="block h-px w-8 bg-dealer-burgundybright transition-[width] duration-500 group-hover:w-12" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 sm:right-10 md:flex"
        aria-hidden="true"
      >
        <span className="dealer-eyebrow">SCROLL</span>
        <motion.span
          className="block h-10 w-px bg-dealer-ivory/30"
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
      </motion.div>
    </section>
  );
};
